import { Routes } from '@angular/router';

import { PermissionGuard, RedirectGuard, canSeeContractorSupplierManagementGuard } from '@walmart/guards';

import { WalmartMasterComponent } from '@walmart/components/layout/master';

import { AuthComponent, AuthGuard, stashAuthReturnUrlGuard, stashPendingEntityRelationshipGuard } from '@shared/auth';
import { UserDataErrorComponent } from '@shared/components/system/user-data-error';
import { PageNotFoundComponent } from '@shared/components/system/page-not-found';
import { authGuardFn } from '@auth0/auth0-angular';
import { WMPerm } from '@walmart/constants';
import { SharedPerm } from '@shared/constants';

export const routes: Routes = [
  {
    path: 'loading',
    component: AuthComponent,
    canActivate: [stashPendingEntityRelationshipGuard, authGuardFn],
    title: $localize`Loading`
  },
  {
    path: 'login-unsuccessful',
    component: UserDataErrorComponent,
    title: $localize`Login Unsuccessful`
  },
  {
    path: '',
    component: WalmartMasterComponent,
    canActivate: [stashAuthReturnUrlGuard, authGuardFn, AuthGuard],
    children: [
      {
        path: 'admin',
        children: [
          {
            path: 'items',
            loadComponent: () => import('@walmart/components/item-admin').then((m) => m.PulseWalmartItemAdminComponent),
            title: $localize`Item Administration`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewItemAdmin]
            }
          },
          {
            path: 'assets',
            loadComponent: () => import('@walmart/components/asset-admin').then((m) => m.PulseWalmartAssetAdminComponent),
            title: $localize`Asset Administration`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewAssetAdmin]
            }
          },
          {
            path: 'pos',
            loadComponent: () =>
              import('./components/admin/purchase-orders/admin-purchase-order-management/admin-purchase-order-management.component').then(
                (m) => m.AdminPurchaseOrderManagementComponent
              ),
            title: $localize`Purchase Order Administration`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewPOAdmin]
            }
          },
          {
            path: 'partners',
            loadComponent: () => import('@walmart/components/partner-administration').then((m) => m.PartnerAdministrationComponent),
            title: $localize`Partner Administration`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewPartnerAdmin]
            }
          },
          {
            path: 'users',
            loadComponent: () => import('@walmart/components/user-management').then((m) => m.TenantUserManagementComponent),
            title: $localize`User Management`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewUserAdmin]
            }
          }
        ]
      },
      {
        path: 'mgmtreports',
        children: [
          {
            path: 'otif',
            loadComponent: () => import('@walmart/components/management-reports/store-project-otif').then((m) => m.ManagementReportsStoreProjectOTIFComponent),
            title: $localize`Store Project OTIF`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewStoreProjectOTIF]
            }
          },
          {
            path: 'quotes',
            loadComponent: () => import('@walmart/components/quotes-by-project-type').then((m) => m.PulseWalmartQuotesByProjectTypeComponent),
            title: $localize`Quotes by Project Type`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewQuotesByProjectType]
            }
          },
          {
            path: 'suppliercompliance',
            loadComponent: () => import('@walmart/components/supplier-compliance').then((m) => m.PulseWalmartSupplierComplianceComponent),
            title: $localize`Supplier Compliance`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewSupplierCompliance]
            }
          },
          {
            path: 'asseterrors',
            loadComponent: () => import('@walmart/components/asset-errors').then((m) => m.PulseWalmartAssetErrorsComponent),
            title: $localize`Asset Errors`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewAssetErrors]
            }
          },
          {
            path: 'preconquotes',
            loadComponent: () => import('@walmart/components/precon-quotes').then((m) => m.PulseWalmartPreConQuotesComponent),
            title: $localize`Pre-Con Quotes`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewPreConQuotes]
            }
          }
        ]
      },
      {
        path: 'ordertracking',
        loadComponent: () => import('@walmart/components/order-tracking/page').then((m) => m.PulseWalmartOrderTrackingPageComponent),
        title: $localize`Order Tracking`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewOrderTracking]
        }
      },
      {
        path: 'suppliercontacts',
        loadComponent: () => import('@walmart/components/supplier/contact-listing').then((m) => m.PulseWalmartSupplierContactsComponent),
        title: $localize`Supplier Contacts`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewSupplierContacts]
        }
      },
      {
        path: 'suppliervitals',
        loadComponent: () => import('@walmart/components/supplier/vitals').then((m) => m.PulseWalmartSupplierVitalsComponent),
        title: $localize`Vitals`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewVitals]
        }
      },
      {
        path: 'supplierquotes',
        loadComponent: () => import('@walmart/components/supplier/quotes').then((m) => m.PulseWalmartSupplierQuotesPageComponent),
        title: $localize`Supplier Quotes`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewSupplierQuotes]
        }
      },
      {
        path: 'storequotes',
        loadComponent: () => import('@walmart/components/store-quotes').then((m) => m.PulseWalmartStoreQuotesComponent),
        title: $localize`Store Quotes`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewStoreQuotes]
        }
      },
      {
        path: 'storequoteadmin',
        loadComponent: () => import('./components/store-quotes-admin-page/store-quotes-admin-page.component').then((m) => m.StoreQuotesAdminPageComponent),
        title: $localize`Quotes Administration`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewQuoteAdmin]
        }
      },
      {
        path: 'supplychainreport',
        loadComponent: () => import('@walmart/components/store-quotes').then((m) => m.PulseWalmartSupplyChainReportPageComponent),
        title: $localize`Supply Chain Report`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewSupplyChainReport]
        }
      },
      {
        path: 'protoreset',
        loadComponent: () => import('@walmart/components/proto-reset').then((m) => m.WalmartProtoResetComponent),
        title: $localize`Proto Reset`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewProtoReset]
        }
      },
      {
        path: 'revisionreasons',
        loadComponent: () => import('@walmart/components/revision-reasons').then((m) => m.PulseWalmartRevisionReasonsComponent),
        title: $localize`Revision Reasons`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewRevisionReasonsReport]
        }
      },
      {
        path: 'rejectionreasons',
        loadComponent: () => import('@walmart/components/rejection-reasons').then((m) => m.PulseWalmartRejectionReasonsComponent),
        title: $localize`Rejection Reasons`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewRejectionReasonsReport]
        }
      },
      {
        path: 'suppliermanagement',
        children: [
          {
            path: 'otif',
            loadComponent: () => import('./components/otif-page/otif-page.component').then((m) => m.OtifPageComponent),
            title: $localize`OTIF`,
            canActivate: [PermissionGuard],
            data: {
              permissions: [WMPerm.ViewSupplierMgmtOTIF]
            }
          },
          {
            path: 'reports',
            children: [
              {
                path: 'globalstores',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-global-store/supplier-management-global-store.component').then(
                    (m) => m.SupplierManagementStoreComponent
                  ),
                title: $localize`Global Stores`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtGlobalStores]
                }
              },
              {
                path: 'globalsuppliers',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-global-supplier/supplier-management-global-supplier.component').then(
                    (m) => m.SupplierManagementGlobalSupplierComponent
                  ),
                title: $localize`Global Suppliers`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtGlobalSuppliers]
                }
              },
              {
                path: 'assets',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-assets/supplier-management-assets.component').then(
                    (m) => m.SupplierManagementAssetsComponent
                  ),
                title: $localize`Assets`,
                canActivate: [PermissionGuard],
                data: {
                  // TODO: New permission for assets page?
                  permissions: [WMPerm.ViewSupplierMgmtGlobalSuppliers]
                }
              },
              {
                path: 'fixturemp',
                loadComponent: () =>
                  import(
                    './components/supplier-management/supplier-management-fixture-major-projects/supplier-management-fixture-major-projects.component'
                  ).then((m) => m.SupplierManagementFixtureMajorProjectsComponent),
                title: $localize`Supplier Management:  Fixture Major Projects`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtFixtureMajorProjects]
                }
              },
              {
                path: 'fixturesp',
                loadComponent: () =>
                  import(
                    './components/supplier-management/supplier-management-fixture-special-projects/supplier-management-fixture-special-projects.component'
                  ).then((m) => m.SupplierManagementFixtureSpecialProjectsComponent),
                title: $localize`Supplier Management:  Fixture Special Projects`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtFixtureSpecialProjects]
                }
              },
              {
                path: 'fixtured',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-fixture-distribution/supplier-management-fixture-distributions.component').then(
                    (m) => m.SupplierManagementFixtureDistributionsComponent
                  ),
                title: $localize`Supplier Management:  Fixture Distributions`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtFixtureDistributions]
                }
              },
              {
                path: 'fixturer',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-fixture-replenishment/supplier-management-fixture-replenishment.component').then(
                    (m) => m.SupplierManagementFixtureReplenishmentComponent
                  ),
                title: $localize`Supplier Management:  Fixture Replenishment`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtFixtureReplenishment]
                }
              },
              {
                path: 'store',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-store-requests/supplier-management-store-requests.component').then(
                    (m) => m.SupplierManagementRequestsComponent
                  ),
                title: $localize`Supplier Management: Store Requests`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtStoreRequests]
                }
              },
              {
                path: 'preconp',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-precon-projects/supplier-management-precon-projects.component').then(
                    (m) => m.SupplierManagementPreConProjectsComponent
                  ),
                title: $localize`Supplier Management: PreCon Projects`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmtPreConProjects]
                }
              },
              {
                path: '**',
                loadComponent: () =>
                  import('./components/supplier-management/supplier-management-page/supplier-management-page.component').then(
                    (m) => m.SupplierManagementPageComponent
                  ),
                title: $localize`Supplier Management`,
                canActivate: [PermissionGuard],
                data: {
                  permissions: [WMPerm.ViewSupplierMgmt]
                }
              }
            ]
          }
        ]
      },
      {
        path: 'consolidatordataeval',
        loadComponent: () => import('@walmart/components/consolidator/data-eval').then((m) => m.PulseWalmartConsolidatorDataEvalComponent),
        title: $localize`Consolidator Data Evaluation`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewConsolidatorDataEval]
        }
      },
      {
        path: 'storeinformation',
        loadComponent: () => import('@walmart/components/projects/store-information').then((m) => m.PulseWalmartStoreInformationComponent),
        title: $localize`Store Information`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewStoreProjects]
        }
      },
      {
        path: 'gcclog',
        loadComponent: () => import('@walmart/components/projects/gcc-log').then((m) => m.PulseWalmartGccLogComponent),
        title: $localize`GCC Log`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewGCCLog]
        }
      },
      {
        path: 'leadtimeviolations',
        loadComponent: () => import('@walmart/components/projects/lead-time-violations').then((m) => m.PulseWalmartLeadTimeViolationsComponent),
        title: $localize`Lead Time Violations`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewLeadTimeViolations]
        }
      },
      {
        path: 'supplierdataeval',
        loadComponent: () => import('@walmart/components/supplier/data-eval').then((m) => m.PulseWalmartSupplierDataEvalComponent),
        title: $localize`Supplier Data Evaluation`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewSupplierDataEval]
        }
      },
      {
        path: 'accountadmin',
        loadComponent: () => import('@walmart/components/partner-administration').then((m) => m.PartnerAdministrationComponent),
        title: $localize`Partner Administration`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewSupplierAdmin, WMPerm.ViewContractorAdmin]
        }
      },
      {
        path: 'supplierhome',
        canActivateChild: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewSupplierHome, WMPerm.ViewVitals]
        },
        children: [
          {
            path: ':suppliernumber',
            loadComponent: () => import('@walmart/components/supplier/home-page').then((m) => m.PulseWalmartSupplierHomeComponent),
            title: $localize`Supplier Home`
          },
          {
            path: '',
            loadComponent: () => import('@walmart/components/supplier/home-page').then((m) => m.PulseWalmartSupplierHomeComponent),
            title: $localize`Supplier Home`
          }
        ]
      },
      {
        path: 'missingpos',
        canActivateChild: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewMissingPO]
        },
        children: [
          {
            path: ':supplierNumber',
            loadComponent: () => import('@walmart/components/supplier/missing-po-listing').then((m) => m.PulseWalmartSupplierMissingPOListingComponent),
            title: $localize`Missing POs`
          },
          {
            path: '',
            loadComponent: () => import('@walmart/components/supplier/missing-po-listing').then((m) => m.PulseWalmartSupplierMissingPOListingComponent),
            title: $localize`Missing POs`
          }
        ]
      },
      {
        path: 'repreport',
        loadComponent: () => import('@walmart/components/rep-report').then((m) => m.PulseWalmartRepReportComponent),
        title: $localize`REP Report`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewRepReport]
        }
      },
      {
        path: 'gcchome',
        loadComponent: () => import('@walmart/components/contractor/home-page').then((m) => m.PulseWalmartContractorHomePageComponent),
        title: $localize`Home`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewGCHome]
        }
      },
      {
        path: 'contractorsuppliermanagement',
        loadComponent: () =>
          import('./components/contractor/contractor-supplier-management/contractor-supplier-management.component').then(
            (m) => m.ContractorSupplierManagementComponent
          ),
        title: $localize`Supplier Management`,
        canActivate: [canSeeContractorSupplierManagementGuard]
      },
      {
        path: 'gccscheduling',
        loadComponent: () => import('@walmart/components/projects/gcc-scheduling').then((m) => m.PulseWalmartGccSchedulingComponent),
        title: $localize`GCC / Scheduling`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewGCCScheduling]
        }
      },
      {
        path: 'marketmanager',
        loadComponent: () => import('@walmart/components/market-manager').then((m) => m.PulseWalmartMarketManagerComponent),
        title: $localize`Market Manager`,
        canActivate: [PermissionGuard],
        data: {
          permissions: [WMPerm.ViewMarketManager]
        }
      },
      {
        path: 'training',
        loadComponent: () => import('@shared/components/training').then((m) => m.PulseTrainingComponent),
        title: $localize`PULSE Training`
      },
      {
        path: 'adminSDE',
        loadComponent: () =>
          import('@walmart/components/supplier/data-eval-advanced-copy-paste').then((m) => m.PulseWalmartSupplierDataEvalAdvancedCopyPasteComponent),
        title: $localize`Admin Supplier Data Eval Testing`,
        canActivate: [PermissionGuard],
        data: { permissions: [SharedPerm.IsLumatrak] }
      },
      {
        path: 'adminCDE',
        loadComponent: () =>
          import('@walmart/components/consolidator/data-eval-advanced-copy-paste').then((m) => m.PulseWalmartConsolidatorDataEvalAdvancedCopyPasteComponent),
        title: $localize`Admin Consolidator Data Eval Testing`,
        canActivate: [PermissionGuard],
        data: { permissions: [SharedPerm.IsLumatrak] }
      },
      {
        path: '',
        component: AuthComponent,
        canActivate: [RedirectGuard],
        pathMatch: 'full'
      },
      { path: '**', component: PageNotFoundComponent, title: $localize`Page Not Found` }
    ]
  }
];
